import { graphql } from '@/__generated__';
import { usePaginatedQuery } from '@/reducers/usePaginatedQuery';
import { MAX_PAGE_SIZE } from './apiConstants';
import { useLoadAllPaginatedResults } from './loadAllPaginatedResults';

export const GetPublicModels = graphql(/* GraphQL */ `
  query PublicModels(
    $accountId: BigInt!
    $projectId: BigInt
    $environmentIds: [BigInt!]
    $uniqueIds: [String!]
    $first: Int
    $after: String
    $includeDependentProjects: Boolean!
  ) {
    account(id: $accountId) {
      publicModels(
        first: $first
        after: $after
        filter: {
          projectId: $projectId
          environmentIds: $environmentIds
          uniqueIds: $uniqueIds
        }
      ) {
        totalCount
        pageInfo {
          endCursor
          hasNextPage
        }
        edges {
          node {
            database
            dbtCoreProject
            deprecationDate
            description
            environmentId
            environmentDeploymentType
            identifier
            latestVersion
            name
            projectId
            relationName
            runGeneratedAt
            lastRunStatus
            schema
            uniqueId
            materializationType
            fqn
            group
            healthIssues
            columnCount
            dependentProjects @include(if: $includeDependentProjects) {
              dbtCoreProject
              defaultEnvironmentId
              environmentId
              dependentModelsCount
              projectId
            }
          }
        }
      }
    }
  }
`);

export const GetPublicModelsPanelList = graphql(/* GraphQL */ `
  query GetPublicModelsPanelList(
    $accountId: BigInt!
    $projectId: BigInt
    $uniqueIds: [String!]
    $first: Int
    $after: String
  ) {
    account(id: $accountId) {
      publicModels(
        first: $first
        after: $after
        filter: { projectId: $projectId, uniqueIds: $uniqueIds }
      ) {
        totalCount
        pageInfo {
          endCursor
          hasNextPage
        }
        edges {
          node {
            dbtCoreProject
            name
            projectId
            relationName
            runGeneratedAt
            uniqueId
            environmentDeploymentType
            environmentId
          }
        }
      }
    }
  }
`);

type MeshProjectProps = {
  first: number;
  after?: string;
  accountId?: number;
  projectId?: number | null;
  environmentIds?: number[] | null;
  uniqueIds?: string[] | null;
  includeDependentProjects?: boolean;
  pause?: boolean;
};

export const usePublicModels = ({
  first,
  after,
  accountId = 0,
  projectId = null,
  environmentIds = null,
  uniqueIds = null,
  includeDependentProjects = false,
  pause,
}: MeshProjectProps) => {
  const query = usePaginatedQuery(
    {
      query: GetPublicModels,
      variables: {
        first,
        after,
        accountId,
        projectId,
        environmentIds,
        uniqueIds,
        includeDependentProjects,
      },
      pause: pause || !accountId || uniqueIds?.length === 0,
    },
    (data) => {
      const publicModels = data?.account?.publicModels;
      const pageInfo = publicModels?.pageInfo;
      if (publicModels == null || pageInfo == null) return undefined;
      return {
        totalCount: publicModels.totalCount ?? null,
        endCursor: pageInfo.endCursor ?? null,
        hasNextPage: pageInfo.hasNextPage,
      };
    },
    (data) => {
      return (data.account?.publicModels?.edges ?? []).map((edge) => edge.node);
    },
    // TODO: we need a cachebusting strategy for Account-level queries
    () => null,
  );

  return query;
};

export type AllPublicModelProps = Omit<MeshProjectProps, 'first'> & {
  loadingStrategy?: 'returnEachPage' | 'waitForAllResults';
};

/**
 * Prefer using `usePublicModels` and attempt to filter to a result set which doesn't
 * require more than 500 models!
 */
export const useAllPublicModels = ({
  loadingStrategy = 'waitForAllResults',
  ...props
}: AllPublicModelProps) => {
  const publicModelsQuery = usePublicModels({ ...props, first: MAX_PAGE_SIZE });
  return useLoadAllPaginatedResults({
    paginatedQuery: publicModelsQuery,
    loadingStrategy,
  });
};

export const usePublicModelsPanelList = ({
  first,
  after,
  accountId = 0,
  projectId = null,
  uniqueIds = null,
  pause,
}: MeshProjectProps) => {
  const query = usePaginatedQuery(
    {
      query: GetPublicModelsPanelList,
      variables: { first, after, accountId, projectId, uniqueIds },
      pause: pause || !accountId || uniqueIds?.length === 0,
    },
    (data) => {
      const publicModels = data?.account?.publicModels;
      const pageInfo = publicModels?.pageInfo;
      if (publicModels == null || pageInfo == null) return undefined;
      return {
        totalCount: publicModels.totalCount ?? null,
        endCursor: pageInfo.endCursor ?? null,
        hasNextPage: pageInfo.hasNextPage,
      };
    },
    (data) => {
      return (data.account?.publicModels?.edges ?? []).map((edge) => edge.node);
    },
    // TODO: we need a cachebusting strategy for Account-level queries
    () => null,
  );

  return useLoadAllPaginatedResults({
    paginatedQuery: query,
    loadingStrategy: 'returnEachPage',
  });
};

export type PublicModelResult = NonNullable<
  NonNullable<ReturnType<typeof usePublicModels>['data']>[number]
>;
